import { useId, useRef, useState } from 'react';
import { type Control, type UseFormSetValue, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { DisclosurePanel } from '@/components/molecules/Disclosure';
import {
    type FieldValue,
    isBlankValue,
    type PenRef,
    type ProviderFieldSchema,
    type ProviderGroup,
} from '@/domain/providerConfig';
import { penKey, toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import { sameValue } from '@/routes/shared/ProviderConfig/form/formLeaves';
import { emptyFormValue, toFormFieldValue } from '@/routes/shared/ProviderConfig/form/formValues';
import {
    type FormValue,
    ProviderSchemaField,
} from '@/routes/shared/ProviderConfig/ProviderSchemaField';
import { unreadableAt } from '@/routes/shared/ProviderConfig/schemaFields';

import type { FieldConflict } from '../form/conflictMerge';
import type { ProviderFormValues } from '../form/formModel';

import { StatusPill } from './StatusPill';
import { conflictAt, needsAttention } from './sectionFields';

import layout from './fieldLayout.module.scss';
import styles from './sections.module.scss';
import siteStyles from './siteField.module.scss';

export interface SiteFieldProps {
    field: ProviderFieldSchema;
    group: ProviderGroup | null;
    pens: PenRef[];
    control: Control<ProviderFormValues>;
    setValue: UseFormSetValue<ProviderFormValues>;
    conflicts: FieldConflict[];
}

interface PenValueProps {
    field: ProviderFieldSchema;
    pen: PenRef;
    control: Control<ProviderFormValues>;
    conflicts: FieldConflict[];
}

/** What the pens held when a site-wide value took their place, and the rows they came from. */
interface Replaced {
    held: ReadonlyMap<number, unknown>;
    stored: Readonly<Record<number, FieldValue>>;
}

function isFormValue(value: unknown): value is FormValue {
    return (
        typeof value === 'string' ||
        (Array.isArray(value) && value.every((entry) => typeof entry === 'string'))
    );
}

/** A pen's own value for a site field; only pens that are or will be connected hold one. */
function PenValue({ field, pen, control, conflicts }: PenValueProps) {
    const base: `pens.${string}` = `pens.${penKey(pen.cageId)}`;
    const connected = useWatch({ control, name: `${base}.connected` });
    if (!connected) return null;
    const name: `pens.${string}.fields.${string}` = `${base}.fields.${toFormKey(field.key)}`;
    return (
        <ProviderSchemaField<ProviderFormValues>
            field={field}
            control={control}
            name={name}
            label={pen.name}
            conflict={conflictAt(conflicts, name)}
        />
    );
}

/**
 * A site setting. Where the pens disagree it says so and opens into one value per pen; a
 * value typed for the whole site replaces the pens' own (R16a), so they are cleared then
 * and restored if the site value is emptied again.
 */
function SiteField({ field, group, pens, control, setValue, conflicts }: SiteFieldProps) {
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const [open, setOpen] = useState(false);
    const formKey = toFormKey(field.key);
    const penPaths = pens.map(
        (pen): `pens.${string}.fields.${string}` => `pens.${penKey(pen.cageId)}.fields.${formKey}`
    );
    const { errors } = useFormState({ control, name: penPaths });
    const penValues = useWatch({ control, name: penPaths });
    // Typed edits included, so emptying the site value again gives those back rather than
    // only the stored ones. By pen, since a reload can change the pen list.
    const replaced = useRef<Replaced | null>(null);
    const connection = group?.connection[field.key];
    const name: `connection.${string}` = `connection.${formKey}`;

    // One pen's unreadable value is enough to say the stored data is not what the box shows.
    const unreadable =
        (group?.rows ?? [])
            .map((row) => unreadableAt(row, field.key))
            .find((entries) => entries.length > 0) ?? [];
    if (!connection?.varies)
        return (
            <ProviderSchemaField<ProviderFormValues>
                field={field}
                control={control}
                name={name}
                conflict={conflictAt(conflicts, name)}
                unreadable={unreadable}
            />
        );

    const perPen = connection.perPen;
    const expanded = open || needsAttention(errors, conflicts, penPaths);
    const listId = `${id}-pens`;

    /**
     * A pen's value back from the snapshot. One it only held from the rows comes from the
     * rows now, which a reload may have refreshed; a typed one is kept. A pen new since the
     * snapshot gets the rows' value; one that left is not in `pens`, so is skipped.
     */
    function restored(cageId: number): FormValue {
        const fresh = toFormFieldValue(field, perPen[cageId] ?? null);
        const snapshot = replaced.current;
        const held = snapshot?.held.get(cageId);
        if (snapshot === null || !isFormValue(held)) return fresh;
        const fromRows = sameValue(held, toFormFieldValue(field, snapshot.stored[cageId] ?? null));
        return fromRows ? fresh : held;
    }

    function handleSiteChange(next: FormValue, previous: FormValue): void {
        const nowBlank = isBlankValue(next);
        if (nowBlank === isBlankValue(previous)) return;
        if (!nowBlank)
            replaced.current = {
                held: new Map(pens.map((pen, index) => [pen.cageId, penValues[index]])),
                stored: perPen,
            };
        pens.forEach((pen, index) => {
            const value = nowBlank ? restored(pen.cageId) : emptyFormValue(field);
            setValue(penPaths[index], value, { shouldDirty: true, shouldValidate: true });
        });
        if (nowBlank) replaced.current = null;
    }

    return (
        <div className={siteStyles.varied}>
            <ProviderSchemaField<ProviderFormValues>
                field={field}
                control={control}
                name={name}
                placeholder={t('providerConfig.connection.varies')}
                conflict={conflictAt(conflicts, name)}
                onValueChange={handleSiteChange}
                trailing={
                    <div className={siteStyles.variesRow}>
                        <StatusPill tone="neutral" label={t('providerConfig.connection.varies')} />
                        <button
                            type="button"
                            className={styles.disclosureToggle}
                            aria-expanded={expanded}
                            aria-controls={listId}
                            onClick={() => setOpen(!expanded)}>
                            {t(
                                expanded
                                    ? 'providerConfig.connection.hidePerPen'
                                    : 'providerConfig.connection.showPerPen'
                            )}
                        </button>
                    </div>
                }
            />
            {/* Stays in the page, so the toggle's aria-controls always resolves. */}
            <DisclosurePanel
                id={listId}
                isOpen={expanded}
                className={classNames(styles.perPen, layout.grid)}>
                {expanded &&
                    pens.map((pen) => (
                        <PenValue
                            key={pen.cageId}
                            field={field}
                            pen={pen}
                            control={control}
                            conflicts={conflicts}
                        />
                    ))}
            </DisclosurePanel>
        </div>
    );
}

export default SiteField;
export { SiteField };
